import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type SelectOption = {
  value: string;
  label: string;
  badge?: ReactNode;
};

export type SelectProps = {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  icon?: ReactNode;
  disabled?: boolean;
  error?: string;
  size?: "xs" | "sm" | "md";
  className?: string;
};

const EMPTY_SENTINEL = "__select_empty__";

export function Select({
  options,
  value,
  onChange,
  placeholder = "Select...",
  icon,
  disabled,
  error,
  size = "md",
  className,
}: SelectProps) {
  // Radix doesn't support empty string values, so map them transparently
  const radixValue = value === "" ? EMPTY_SENTINEL : value;
  const handleChange = (v: string) => onChange?.(v === EMPTY_SENTINEL ? "" : v);

  return (
    <SelectPrimitive.Root value={radixValue} onValueChange={handleChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        className={cn(
          "w-full flex items-center justify-between gap-2 border text-white select-none",
          size === "xs" && "px-2 py-0.5 h-6 text-xs rounded bg-surface-700",
          size === "sm" && "h-8 px-3 text-xs rounded-lg bg-surface-800",
          size === "md" && "px-4 py-2 text-sm rounded-lg bg-surface-800",
          "placeholder-content-muted transition-colors duration-200",
          "focus:outline-none focus:ring-0",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          error ? "border-error-500" : "border-surface-700 hover:border-brand-500",
          className,
        )}
      >
        <span className="flex items-center gap-2 min-w-0 truncate">
          {icon && <span className="shrink-0 [&_svg]:w-5 [&_svg]:h-5">{icon}</span>}
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className={cn("shrink-0 text-content-muted", size === "xs" ? "w-3 h-3" : "w-4 h-4")}
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            "z-70 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg",
            "bg-surface-850 border border-surface-700 shadow-xl",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
          )}
          position="popper"
          sideOffset={4}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => {
              const optValue = option.value === "" ? EMPTY_SENTINEL : option.value;
              return (
                <SelectPrimitive.Item
                  key={optValue}
                  value={optValue}
                  className={cn(
                    "relative flex items-center gap-2 px-3 py-1.5 text-sm text-white cursor-pointer",
                    "outline-none transition-colors rounded-md",
                    "data-[highlighted]:bg-surface-700 data-[state=checked]:text-brand-400",
                  )}
                >
                  {option.badge && <span className="shrink-0">{option.badge}</span>}
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="ml-auto">
                    <Check className="w-4 h-4 text-brand-500" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              );
            })}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
